/**
 * Modal Component - Generic modal with overlay and different types
 */

import { useEffect } from 'react';
import { useModal } from '../../context/ModalContext';
import './Modal.css';

const Modal = () => {
  const { modalState, handleConfirm, handleCancel, closeModal } = useModal();
  
  const {
    isOpen,
    type,
    title,
    content,
    confirmText,
    cancelText,
    showConfirm,
    showCancel,
    customComponent,
    size,
    closable,
  } = modalState;

  // Handle ESC key to close modal
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape' && isOpen && closable) {
        closeModal();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscKey);
      // Prevent body scrolling when modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, closable, closeModal]);

  // Handle overlay click to close modal
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget && closable) {
      closeModal();
    }
  };

  if (!isOpen) {
    return null;
  }

  // Get modal type class for styling
  const getModalTypeClass = () => {
    switch (type) {
      case 'error': return 'modal-error';
      case 'warning': return 'modal-warning';
      case 'info': return 'modal-info';
      case 'confirm': return 'modal-confirm';
      default: return 'modal-generic';
    }
  };

  // Get modal size class
  const getSizeClass = () => {
    switch (size) {
      case 'small': return 'modal-small';
      case 'large': return 'modal-large';
      default: return 'modal-medium';
    }
  };



  return (
    <div className="modal-overlay" onClick={handleOverlayClick}>
      <div className={`modal-container ${getModalTypeClass()} ${getSizeClass()}`}>
        {/* Top Bar - Visual element only (for default modals, not custom components) */}
        {!customComponent && <div className="modal-top-bar" />}
        
        {/* Header */}
        <div className={`modal-header ${!customComponent ? 'centered' : ''}`}>
          <div className="modal-title-container">
            {title && <h2 className="modal-title">{title}</h2>}
          </div>
          {closable && customComponent && (
            <button 
              className="modal-close-button"
              onClick={closeModal}
              type="button"
              aria-label="Close modal"
            >
              <ion-icon name="close-outline" />
            </button>
          )}
        </div>

        {/* Content */}
        <div className={`modal-content ${(customComponent) ? 'custom' : ''}`}>
          {customComponent ? (
            customComponent
          ) : (
            <div className="modal-text">
              {typeof content === 'string' ? (
                <p>{content}</p>
              ) : (
                content
              )}
            </div>
          )}
        </div>

        {/* Footer with action buttons */}
        {(showCancel || showConfirm) && (
          <div className="modal-footer">
            {showCancel && (
              <button 
                className="modal-button modal-button-cancel"
                onClick={handleCancel}
                type="button"
              >
                {cancelText}
              </button>
            )}
            {showConfirm && (
              <button 
                className="modal-button modal-button-confirm"
                onClick={handleConfirm}
                type="button"
              >
                {confirmText}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Modal;
